import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddressAutocomplete from './AddressAutocomplete';
import './FareEstimator.css';

// Round trip rates from General Tariff No. GT-1 (WMATC No. 4086).
// One way rates set by Absolute Mobility (not yet in the filed tariff).
// Update here if rates change.
export const RATES = {
  roundTrip: {
    label: 'Round trip',
    baseRate: 180,       // covers the first includedMiles (was $150, raised 20%)
    includedMiles: 10,   // round trip miles covered by the base rate
    perMile: 4.2,        // each mile beyond the included miles (was $3.50)
    minimumFare: 190,    // lowest ride price before wait time
    discount: 0.15,      // 15% off the first round trip
  },
  oneWay: {
    label: 'One way',
    baseRate: 100,
    includedMiles: 5,
    perMile: 4.2,        // was $3.50
    minimumFare: 110,    // lowest ride price
    discount: 0.10,      // 10% off the first one way ride
  },
  wheelchairSurcharge: 24, // once per trip, wheelchair rides only (was $20)
  waitPerHour: 42,         // driver waits during the appointment, round trips only (was $35)
  waitMinimumHours: 1,     // wait is billed in whole hours, 1 hour minimum
};

// Appointment lengths the rider can choose on a round trip (in hours)
const APPOINTMENT_OPTIONS = [0.5, 1, 1.5, 2, 2.5, 3, 4];
const hoursLabel = (h) => (h < 1 ? `${h * 60} minutes` : `${h} hour${h === 1 ? '' : 's'}`);

const API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY;
const DMV_BIAS = 'proximity:-77.05,38.95';

// Only suggest addresses in our service area: Maryland, DC and Northern Virginia
// (box corners: west longitude, south latitude, east longitude, north latitude)
const SERVICE_AREA = 'rect:-79.5,37.9,-75.0,39.75';
const METERS_PER_MILE = 1609.344;

const money = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const round2 = (n) => Math.round(n * 100) / 100;

// Works out the price from the trip miles
export function calculateFare(miles, tripType, serviceType, appointmentHours = 0) {
  const rate = RATES[tripType];
  // Round trips include the driver's wait, rounded up to whole hours with a 1 hour minimum
  const waitHours = tripType === 'roundTrip'
    ? Math.max(RATES.waitMinimumHours, Math.ceil(appointmentHours))
    : 0;
  const waitCharge = waitHours * RATES.waitPerHour;
  const extraMiles = Math.max(0, Math.round((miles - rate.includedMiles) * 10) / 10);
  const mileageCharge = round2(extraMiles * rate.perMile);
  const surcharge = serviceType === 'wheelchair' ? RATES.wheelchairSurcharge : 0;
  // The ride itself (base, miles, surcharge) never goes below the minimum fare; wait time is added on top
  const rideCharge = round2(rate.baseRate + mileageCharge + surcharge);
  const minimumAdjustment = round2(Math.max(0, rate.minimumFare - rideCharge));
  const total = round2(rideCharge + minimumAdjustment + waitCharge);
  const discount = round2(total * rate.discount);
  return {
    extraMiles, mileageCharge, surcharge, minimumAdjustment, waitHours, waitCharge,
    total, discount, final: round2(total - discount),
  };
}

// Finds coordinates for an address the rider typed without picking a suggestion
async function geocode(text) {
  const url =
    'https://api.geoapify.com/v1/geocode/search?text=' + encodeURIComponent(text) +
    '&filter=' + SERVICE_AREA + '&bias=' + DMV_BIAS + '&limit=1&format=json&apiKey=' + API_KEY;
  const res = await fetch(url);
  if (!res.ok) throw new Error('lookup failed');
  const data = await res.json();
  const hit = data.results && data.results[0];
  if (!hit) throw new Error('not found');
  return { lat: hit.lat, lon: hit.lon };
}

// One way driving distance in miles
async function drivingMiles(from, to) {
  const url =
    'https://api.geoapify.com/v1/routing?waypoints=' +
    `${from.lat},${from.lon}%7C${to.lat},${to.lon}` +
    '&mode=drive&apiKey=' + API_KEY;
  const res = await fetch(url);
  if (!res.ok) throw new Error('route failed');
  const data = await res.json();
  const meters = data.features && data.features[0] && data.features[0].properties.distance;
  if (!meters) throw new Error('no route');
  return meters / METERS_PER_MILE;
}

function FareEstimator() {
  const navigate = useNavigate();
  const [pickup, setPickup] = useState('');
  const [pickupLoc, setPickupLoc] = useState(null);
  const [dropoff, setDropoff] = useState('');
  const [dropoffLoc, setDropoffLoc] = useState(null);
  const [serviceType, setServiceType] = useState('wheelchair');
  const [tripType, setTripType] = useState('roundTrip');
  const [appointmentHours, setAppointmentHours] = useState(1);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEstimate(null);
    if (!API_KEY) {
      setError('Estimates are unavailable right now. Please call us at (202) 816-2344.');
      return;
    }
    setLoading(true);
    try {
      const from = pickupLoc || (await geocode(pickup));
      const to = dropoffLoc || (await geocode(dropoff));
      const oneWay = await drivingMiles(from, to);
      const miles = Math.round((tripType === 'roundTrip' ? oneWay * 2 : oneWay) * 10) / 10;
      setEstimate({ miles, tripType, serviceType, ...calculateFare(miles, tripType, serviceType, appointmentHours) });
    } catch (err) {
      setError("We couldn't find a driving route for those addresses. Please check them, or call us at (202) 816-2344.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="estimator-card">
      <div className="estimator-header">
        <span className="estimator-badge">15% OFF ROUND TRIPS | 10% OFF ONE WAY</span>
        <h2>Get a Free Estimate</h2>
        <p>Enter your trip and see your price instantly, with your first ride discount applied.</p>
      </div>

      <form className="estimator-form" onSubmit={handleSubmit}>
        <div className="estimator-field">
          <label htmlFor="est-pickup">Pickup Address</label>
          <AddressAutocomplete
            id="est-pickup"
            name="estPickup"
            placeholder="Start typing the address"
            value={pickup}
            onChange={(text) => { setPickup(text); setPickupLoc(null); }}
            onSelect={(r) => setPickupLoc({ lat: r.lat, lon: r.lon })}
            required
          />
        </div>
        <div className="estimator-field">
          <label htmlFor="est-dropoff">Dropoff Address</label>
          <AddressAutocomplete
            id="est-dropoff"
            name="estDropoff"
            placeholder="Start typing the address"
            value={dropoff}
            onChange={(text) => { setDropoff(text); setDropoffLoc(null); }}
            onSelect={(r) => setDropoffLoc({ lat: r.lat, lon: r.lon })}
            required
          />
        </div>
        <div className="estimator-field">
          <label htmlFor="est-service">Service Type</label>
          <select id="est-service" value={serviceType} onChange={(e) => { setServiceType(e.target.value); setEstimate(null); }}>
            <option value="wheelchair">Wheelchair Transport</option>
            <option value="ambulatory">Ambulatory / Sitting</option>
          </select>
        </div>
        <div className="estimator-field">
          <span className="trip-type-label">Trip Type</span>
          <div className="trip-toggle" role="radiogroup" aria-label="Trip type">
            {['roundTrip', 'oneWay'].map((type) => (
              <button
                key={type}
                type="button"
                role="radio"
                aria-checked={tripType === type}
                className={tripType === type ? 'trip-option selected' : 'trip-option'}
                onClick={() => { setTripType(type); setEstimate(null); }}
              >
                {RATES[type].label}
                <small>{Math.round(RATES[type].discount * 100)}% off first ride</small>
              </button>
            ))}
          </div>
        </div>
        {tripType === 'roundTrip' && (
          <div className="estimator-field">
            <label htmlFor="est-appointment">How long is your appointment?</label>
            <select
              id="est-appointment"
              value={appointmentHours}
              onChange={(e) => { setAppointmentHours(Number(e.target.value)); setEstimate(null); }}
            >
              {APPOINTMENT_OPTIONS.map((h) => (
                <option key={h} value={h}>{hoursLabel(h)}</option>
              ))}
            </select>
            <small className="estimator-field-hint">
              Our driver waits for you. Wait time is ${RATES.waitPerHour} per hour, 1 hour minimum.
              Longer than 4 hours? Call us.
            </small>
          </div>
        )}
        <p className="estimator-trip-note">
          {tripType === 'roundTrip'
            ? 'Round trip: pickup to dropoff, we wait, and bring you back.'
            : 'One way: pickup to dropoff only.'}
        </p>
        <button type="submit" className="btn-primary estimator-submit" disabled={loading}>
          {loading ? 'Calculating...' : 'Calculate My Price'}
        </button>
      </form>

      {error && <div className="estimator-error" role="alert">{error}</div>}

      {estimate && (
        <div className="estimator-result" role="status">
          <div className="result-lines">
            <div className="result-line">
              <span>Base rate ({RATES[estimate.tripType].label.toLowerCase()}, first {RATES[estimate.tripType].includedMiles} miles)</span>
              <span>{money(RATES[estimate.tripType].baseRate)}</span>
            </div>
            <div className="result-line">
              <span>
                Extra miles: {estimate.extraMiles.toFixed(1)} × {money(RATES[estimate.tripType].perMile)}
                <small> ({estimate.miles.toFixed(1)} miles {RATES[estimate.tripType].label.toLowerCase()})</small>
              </span>
              <span>{money(estimate.mileageCharge)}</span>
            </div>
            {estimate.surcharge > 0 && (
              <div className="result-line">
                <span>Wheelchair securement and assistance</span>
                <span>{money(estimate.surcharge)}</span>
              </div>
            )}
            {estimate.minimumAdjustment > 0 && (
              <div className="result-line">
                <span>Minimum fare adjustment ({money(RATES[estimate.tripType].minimumFare)} minimum)</span>
                <span>{money(estimate.minimumAdjustment)}</span>
              </div>
            )}
            {estimate.waitCharge > 0 && (
              <div className="result-line">
                <span>Wait time: {estimate.waitHours} hr × {money(RATES.waitPerHour)}</span>
                <span>{money(estimate.waitCharge)}</span>
              </div>
            )}
            <div className="result-line result-subtotal">
              <span>Regular price</span>
              <span className="strike">{money(estimate.total)}</span>
            </div>
            <div className="result-line result-discount">
              <span>First ride discount ({Math.round(RATES[estimate.tripType].discount * 100)}%)</span>
              <span>−{money(estimate.discount)}</span>
            </div>
          </div>
          <div className="result-final">
            <span>Your first ride price</span>
            <strong>{money(estimate.final)}</strong>
          </div>
          <button type="button" className="btn-primary estimator-book" onClick={() => navigate('/book')}>
            Book This Ride
          </button>
          <p className="estimator-fine-print">
            Estimate based on driving distance.
            {estimate.tripType === 'roundTrip'
              ? ` Wait time beyond your selected appointment length is extra at $${RATES.waitPerHour} per hour.`
              : ''}
            {' '}The discount applies to your first ride only. We confirm the final price when we call to confirm your booking.
          </p>
        </div>
      )}

      <div className="estimator-regulars">
        <strong>Riding with us regularly?</strong> Ask about our returning customer and recurring ride pricing.
        Call <a href="tel:+12028162344">(202) 816-2344</a>.
      </div>
    </div>
  );
}

export default FareEstimator;
